import { Card, CardContent, CardHeader, Box, Typography } from "@mui/material";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatValue, pickSeriesColor, type BaseChartConfig, type Widget } from "./types";

const AXIS_COLOR = "#374151";
const GRID_COLOR = "rgba(0,0,0,0.08)";
const TOOLTIP_BG = "rgba(15, 23, 42, 0.92)";
const TOOLTIP_TEXT = "#f9fafb";
const PALETTE = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899"];

interface LineConfig extends BaseChartConfig {
  /** Optional explicit series declaration. When absent, derives series
   *  from every numeric column in the first data point (excluding label). */
  series?: { key: string; label?: string; color?: string }[];
}

export function LineCard({ widget }: { widget: Widget }) {
  const cfg = widget.config as LineConfig;
  const rawPoints = widget.data?.points ?? [];
  const points = rawPoints.filter((p): p is Record<string, unknown> => p !== null && typeof p === "object");
  const fmt = cfg.format ?? "number";

  const series = cfg.series?.length
    ? cfg.series
    : points.length
      ? Object.entries(points[0])
          .filter(([k, v]) => k !== "label" && typeof v === "number")
          .map(([k]) => ({ key: k }))
      : [{ key: "value" }];

  return (
    <Card variant="outlined">
      {widget.title && (
        <CardHeader
          title={widget.title}
          titleTypographyProps={{ variant: "subtitle1", fontWeight: 700, letterSpacing: "-0.01em" }}
          subheaderTypographyProps={{ variant: "caption", color: "text.secondary" }}
          sx={{ pb: 1 }}
        />
      )}
      <CardContent sx={{ pt: 0 }}>
        {points.length === 0 ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              {widget.data?.error ? `No data (${widget.data.error})` : "No data."}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ width: "100%", height: { xs: 320, sm: 380, md: 420 } }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={points} margin={{ top: 8, right: 24, left: 24, bottom: 32 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} />
                <XAxis
                  dataKey="label"
                  tick={{ fill: AXIS_COLOR, fontSize: 11 }}
                  stroke={AXIS_COLOR}
                />
                <YAxis
                  tick={{ fill: AXIS_COLOR, fontSize: 11 }}
                  stroke={AXIS_COLOR}
                  tickFormatter={(v: number) => formatValue(v, fmt)}
                />
                <Tooltip
                  contentStyle={{ backgroundColor: TOOLTIP_BG, border: "none", borderRadius: 6, color: TOOLTIP_TEXT }}
                  labelStyle={{ color: TOOLTIP_TEXT, fontWeight: 600 }}
                  itemStyle={{ color: TOOLTIP_TEXT }}
                  formatter={(v: number, name: string) => [formatValue(v, fmt), name]}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {cfg.target_line !== undefined && (
                  <ReferenceLine
                    y={cfg.target_line}
                    stroke="#ef4444"
                    strokeDasharray="4 4"
                    strokeWidth={1.5}
                    label={{
                      value: `Target ${formatValue(cfg.target_line, fmt)}`,
                      fill: "#ef4444",
                      fontSize: 11,
                      position: "insideTopRight",
                    }}
                  />
                )}
                {series.map((s, i) => (
                  <Line
                    key={s.key}
                    dataKey={s.key}
                    name={s.label ?? s.key}
                    stroke={s.color ?? cfg.color ?? PALETTE[i % PALETTE.length]}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
